import { CSSProperties, Fragment } from 'react';

import { QLogo } from '@quizzach/assets';
import {
	ContentPattern,
	splitIngredient,
	StandardQuestionAnalysis,
} from '@quizzach/utils';
import {
	AuthorNote,
	EventTimeline,
	FactGrid,
	HandNote,
	OptList,
	QuestionContainer,
	QuestionText,
	RecipeCard,
	VerseBlock,
} from '@quizzach/play-styles';

// ------------------------------------------------------------------ //
// Pattern-specific bodies for standard question/answer slides. One
// component serves desktop and mobile — the styled bits mirror the
// QuestionContainer padding convention the other slide bits use, so
// everything renders as siblings of the container like OptList does.
// Detection lives in utils/contentPattern.ts (frontend-only).
// ------------------------------------------------------------------ //

const Html = ({ html }: { html: string }) => (
	<div dangerouslySetInnerHTML={{ __html: html }} />
);

const plainText = (html: string): string =>
	html
		.replace(/<[^>]+>/g, ' ')
		.replace(/&nbsp;/gi, ' ')
		.replace(/\s+/g, ' ')
		.trim();

const stripHasText = (html: string): boolean => plainText(html).length > 0;

const QuestionBlock = ({ html }: { html: string }) => (
	<QuestionText>
		<Html html={html} />
	</QuestionText>
);

const PatternContent = ({
	pattern,
	questionHtml,
	dim,
	color,
}: {
	pattern: ContentPattern | null;
	questionHtml: string;
	dim?: boolean;
	color: string;
}) => {
	// no pattern — the plain question (the note still rendered by the parent)
	if (!pattern) {
		return (
			<QuestionContainer color={color}>
				<QuestionBlock html={questionHtml} />
			</QuestionContainer>
		);
	}

	switch (pattern.kind) {
		case 'lyrics':
			// mid-text verse blocks carry an intro — the verse renders between
			// the intro and the closing ask, preserving the reading order
			return (
				<>
					{pattern.introHtml && stripHasText(pattern.introHtml) && (
						<QuestionContainer color={color}>
							<QuestionBlock html={pattern.introHtml} />
						</QuestionContainer>
					)}
					<VerseBlock $dim={dim}>
						<div
							className='verse-text'
							dangerouslySetInnerHTML={{ __html: pattern.verseHtml }}
						/>
					</VerseBlock>
					{stripHasText(pattern.questionHtml) && (
						<QuestionContainer color={color}>
							<QuestionBlock html={pattern.questionHtml} />
						</QuestionContainer>
					)}
				</>
			);

		case 'quote':
			// an end-positioned quote carries the ask as introHtml — the
			// original reading order (ask above, quote below) is kept
			return (
				<>
					{pattern.introHtml && stripHasText(pattern.introHtml) && (
						<QuestionContainer color={color}>
							<QuestionBlock html={pattern.introHtml} />
						</QuestionContainer>
					)}
					<VerseBlock $quote $dim={dim}>
						<div
							className='verse-text'
							dangerouslySetInnerHTML={{ __html: pattern.quoteHtml }}
						/>
					</VerseBlock>
					{stripHasText(pattern.questionHtml) && (
						<QuestionContainer color={color}>
							<QuestionBlock html={pattern.questionHtml} />
						</QuestionContainer>
					)}
				</>
			);

		case 'yearEvents':
			// prose keeps its original place around the timeline
			return (
				<>
					{stripHasText(pattern.introHtml) && (
						<QuestionContainer color={color}>
							<QuestionBlock html={pattern.introHtml} />
						</QuestionContainer>
					)}
					<EventTimeline $dim={dim}>
						{pattern.events.map((event, index) => (
							<div className='ev' key={index}>
								<QLogo />
								<span>{event}</span>
							</div>
						))}
					</EventTimeline>
					{stripHasText(pattern.outroHtml) && (
						<QuestionContainer color={color}>
							<QuestionBlock html={pattern.outroHtml} />
						</QuestionContainer>
					)}
				</>
			);

		case 'subParts':
			return (
				<>
					{stripHasText(pattern.introHtml) && (
						<QuestionContainer color={color}>
							<QuestionBlock html={pattern.introHtml} />
						</QuestionContainer>
					)}
					<OptList>
						{pattern.parts.map((part) => (
							<div className='opt' key={part.label}>
								<span className='L'>{part.label}</span>
								<span
									className='t'
									dangerouslySetInnerHTML={{ __html: part.html }}
								/>
							</div>
						))}
					</OptList>
				</>
			);

		case 'labeledFacts':
			// krug 7 GO: dosje-mreža — gold handwritten labels, facts in ink
			return (
				<>
					{stripHasText(pattern.introHtml) && (
						<QuestionContainer color={color}>
							<QuestionBlock html={pattern.introHtml} />
						</QuestionContainer>
					)}
					<FactGrid $dim={dim}>
						{pattern.facts.map((fact, index) => (
							<div className='fact' key={index}>
								<span className='fl'>{fact.label}</span>
								<div
									className='fv'
									dangerouslySetInnerHTML={{ __html: fact.html }}
								/>
							</div>
						))}
					</FactGrid>
					{stripHasText(pattern.outroHtml) && (
						<QuestionContainer color={color}>
							<QuestionBlock html={pattern.outroHtml} />
						</QuestionContainer>
					)}
				</>
			);

		case 'numberedList':
			// digits get the connection-column indicator treatment; the
			// question prose keeps its slot before and/or after the list
			return (
				<>
					{stripHasText(pattern.introHtml) && (
						<QuestionContainer color={color}>
							<QuestionBlock html={pattern.introHtml} />
						</QuestionContainer>
					)}
					<OptList>
						{pattern.items.map((item) => (
							<div className='opt' key={item.label}>
								<span className='L'>{item.label}</span>
								<span
									className='t'
									dangerouslySetInnerHTML={{ __html: item.html }}
								/>
							</div>
						))}
					</OptList>
					{stripHasText(pattern.outroHtml) && (
						<QuestionContainer color={color}>
							<QuestionBlock html={pattern.outroHtml} />
						</QuestionContainer>
					)}
				</>
			);

		case 'anagram':
			return (
				<>
					<QuestionContainer color={color}>
						<QuestionBlock html={pattern.introHtml} />
					</QuestionContainer>
					<HandNote $scramble $below $dim={dim}>
						{pattern.scramble}
					</HandNote>
				</>
			);

		case 'ordering':
			// krug 7 (founder): members in gold Caveat handwriting, like the
			// link clues — no pills anywhere in the patterns
			return (
				<>
					<QuestionContainer color={color}>
						<QuestionBlock html={pattern.questionHtml} />
					</QuestionContainer>
					{pattern.items.length > 0 && (
						<HandNote $below $dim={dim}>
							{pattern.items.join(', ')}
						</HandNote>
					)}
				</>
			);

		case 'link':
			// krug 7 (founder): clues in gold Caveat handwriting, not pills —
			// same voice as the rukopisni popis
			return (
				<>
					<HandNote $dim={dim}>{pattern.clues.join(', ')}</HandNote>
					<QuestionContainer color={color}>
						<QuestionBlock html={pattern.questionHtml} />
					</QuestionContainer>
				</>
			);

		case 'handList':
			return (
				<>
					<HandNote $dim={dim}>{pattern.items.join(', ')}</HandNote>
					<QuestionContainer color={color}>
						<QuestionBlock html={pattern.questionHtml} />
					</QuestionContainer>
				</>
			);

		case 'recipe': {
			// ingredients-only lists: a short leading question speaks as the
			// card's handwritten label (»prepoznajte ovo jelo:«) instead of
			// »sastojci«, so it doesn't repeat around the card (founder,
			// 2026-08-27); otherwise the prose keeps its original place —
			// intro above the card, closing ask below it
			const introText = plainText(pattern.introHtml);
			const questionAsLabel =
				!stripHasText(pattern.prepHtml) &&
				!stripHasText(pattern.outroHtml) &&
				introText.length > 0 &&
				introText.length <= 70;
			return (
				<>
					{!questionAsLabel && stripHasText(pattern.introHtml) && (
						<QuestionContainer color={color}>
							<QuestionBlock html={pattern.introHtml} />
						</QuestionContainer>
					)}
					<RecipeCard $dim={dim}>
						<div className='rec-col'>
							<span className='rec-label'>
								{questionAsLabel ? introText : 'sastojci'}
							</span>
							<div className='rec-grid'>
								{pattern.ingredients.map((item, index) => {
									const { amt, name } = splitIngredient(item);
									return (
										<Fragment key={index}>
											<span className='amt'>{amt}</span>
											<span className='ing'>{name}</span>
										</Fragment>
									);
								})}
							</div>
						</div>
						{stripHasText(pattern.prepHtml) && (
							<div className='rec-col'>
								<span className='rec-label'>priprema</span>
								<p
									className='rec-prep'
									dangerouslySetInnerHTML={{ __html: pattern.prepHtml }}
								/>
							</div>
						)}
					</RecipeCard>
					{stripHasText(pattern.outroHtml) && (
						<QuestionContainer color={color}>
							<QuestionBlock html={pattern.outroHtml} />
						</QuestionContainer>
					)}
				</>
			);
		}
	}
};

/** The author's napomena line — shared by every question type that
 *  carries free question text (standard, abcd, visual, estimation,
 *  connection). Renders nothing without a note. */
export const QuestionNote = ({
	note,
	dim,
	style,
}: {
	note: string | null;
	dim?: boolean;
	style?: CSSProperties;
}) =>
	note ? (
		<AuthorNote $dim={dim} style={style}>
			<span className='n-label'>napomena</span>
			<span className='n-text'>{note}</span>
		</AuthorNote>
	) : null;

/**
 * The full pattern-aware body of a standard slide (question text plus
 * pattern bits plus the author's napomena) — everything except the
 * points row and the answer glass, which the layouts keep rendering.
 */
export const StandardPatternBody = ({
	analysis,
	color,
	dim,
}: {
	analysis: StandardQuestionAnalysis;
	color: string;
	dim?: boolean;
}) => (
	<>
		{analysis.kicker && <HandNote $dim={dim}>{analysis.kicker}</HandNote>}
		<PatternContent
			pattern={analysis.pattern}
			questionHtml={analysis.questionHtml}
			dim={dim}
			color={color}
		/>
		<QuestionNote note={analysis.note} dim={dim} />
	</>
);
